import React, { FunctionComponent, ReactNode, useState } from 'react'
import { View } from 'react-native'

import styled, { useTheme } from 'styled-components/native'

import DataTable from '@lyrahealth-inc/ui-core/lib/molecules/table/DataTable'
import { BodyText } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/bodyText/BodyText'
import { SecondaryButton } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/secondaryButton/SecondaryButton'
import { Title } from '@lyrahealth-inc/ui-core-crossplatform/src/atoms/title/Title'
import { CollapsibleSection } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/collapsibleSection/CollapsibleSection'
import { InlineErrorBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineErrorBanner/InlineErrorBanner'
import { InlineSuccessBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineSuccessBanner/InlineSuccessBanner'
import { InlineWarningBanner } from '@lyrahealth-inc/ui-core-crossplatform/src/molecules/inlineWarningBanner/InlineWarningBanner'
import { BodyTextSize, TitleSize } from '@lyrahealth-inc/ui-core-crossplatform/src/styles/typeStyles'
import { tID } from '@lyrahealth-inc/ui-core-crossplatform/src/utils/utils'

import { NpCatalogImportCounts, NpCatalogImportResponse } from '../data/npCatalogImportApi'

const PAGE_SIZE = 10

type StatGroup = {
  title: string
  stats: { key: keyof NpCatalogImportCounts; label: string; destructive?: boolean }[]
}

const STAT_GROUPS: StatGroup[] = [
  {
    title: 'Rows',
    stats: [
      { key: 'rows_read', label: 'Read' },
      { key: 'rows_skipped', label: 'Skipped' },
      { key: 'rows_with_errors', label: 'With errors', destructive: true },
    ],
  },
  {
    title: 'Measures',
    stats: [
      { key: 'measures_created', label: 'Created' },
      { key: 'measures_updated', label: 'Updated' },
      { key: 'measures_unchanged', label: 'Unchanged' },
      { key: 'parent_links_changed', label: 'Parent links changed' },
    ],
  },
  {
    title: 'Batteries',
    stats: [
      { key: 'batteries_created', label: 'Created' },
      { key: 'batteries_reactivated', label: 'Reactivated' },
      { key: 'batteries_deprecated', label: 'Deprecated', destructive: true },
      { key: 'battery_measures_added', label: 'Measures added' },
      { key: 'battery_measures_removed', label: 'Measures removed', destructive: true },
    ],
  },
]

const StatsRow = styled.View(({ theme }) => ({
  flexDirection: 'row',
  flexWrap: 'wrap',
  gap: theme.spacing['16px'],
}))

const StatGroupCard = styled.View(({ theme }) => ({
  flexGrow: 1,
  minWidth: '240px',
  padding: theme.spacing['16px'],
  gap: theme.spacing['8px'],
  borderRadius: '12px',
  borderWidth: '1px',
  borderColor: theme.colors.borderDefault,
  backgroundColor: theme.colors.backgroundPrimary,
}))

const StatLine = styled.View({
  flexDirection: 'row',
  justifyContent: 'space-between',
})

const SectionContent = styled.View(({ theme }) => ({
  padding: theme.spacing['16px'],
  gap: theme.spacing['8px'],
}))

const RawResponse = styled.ScrollView(({ theme }) => ({
  maxHeight: 320,
  padding: theme.spacing['12px'],
  borderRadius: '8px',
  backgroundColor: theme.colors.backgroundSecondary,
}))

const COPY_LABELS = { idle: 'Copy to clipboard', copied: 'Copied!', failed: 'Copy failed, select the text instead' }

const RawResponseSection: FunctionComponent<{ summary: NpCatalogImportResponse }> = ({ summary }) => {
  const [copyStatus, setCopyStatus] = useState<'idle' | 'copied' | 'failed'>('idle')
  const json = JSON.stringify(summary, null, 2)

  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(json)
      setCopyStatus('copied')
    } catch {
      // Clipboard access can be denied (permissions, document not focused); the JSON can still be selected below.
      setCopyStatus('failed')
    }
  }

  return (
    <Section id='raw-response' title='Raw response'>
      <View style={{ alignItems: 'flex-start' }}>
        <SecondaryButton
          isSmall
          text={COPY_LABELS[copyStatus]}
          testID={tID('NpProgramAdmin-raw-response-copy')}
          onPress={onCopy}
        />
      </View>
      <RawResponse nestedScrollEnabled>
        <BodyText size={BodyTextSize.SMALL} text={json} />
      </RawResponse>
    </Section>
  )
}

const Section: FunctionComponent<{ id: string; title: string; open?: boolean; children: ReactNode }> = ({
  id,
  title,
  open = false,
  children,
}) => (
  <CollapsibleSection
    testIDPrefix={tID(`NpProgramAdmin-${id}`)}
    title={title}
    initiallyOpen={open}
    content={<SectionContent>{children}</SectionContent>}
  />
)

const Table = ({ data, columns }: { data: Dict[]; columns: { Header: string; accessor: string }[] }) => (
  <DataTable
    data={data}
    columns={columns}
    pagination={data.length > PAGE_SIZE}
    pageSize={Math.min(data.length, PAGE_SIZE)}
    striped
    resizable={false}
  />
)

const formatValue = (value: unknown) => (value === null || value === undefined || value === '' ? '–' : String(value))

const StatGroups: FunctionComponent<{ counts: NpCatalogImportCounts }> = ({ counts }) => {
  const { colors } = useTheme()
  return (
    <StatsRow>
      {STAT_GROUPS.map(({ title, stats }) => (
        <StatGroupCard key={title}>
          <Title size={TitleSize.XSMALL} level='3' text={title} />
          {stats.map(({ key, label, destructive }) => (
            <StatLine key={key}>
              <BodyText text={label} size={BodyTextSize.SMALL} />
              <BodyText
                text={String(counts[key])}
                size={BodyTextSize.SMALL}
                testID={tID(`NpProgramAdmin-count-${key}`)}
                color={destructive && counts[key] > 0 ? colors.textWarning : undefined}
              />
            </StatLine>
          ))}
        </StatGroupCard>
      ))}
    </StatsRow>
  )
}

type CatalogImportReviewProps = {
  summary: NpCatalogImportResponse
}

/** Reviews a catalog import summary: the dry-run preview, or the summary of a rejected confirmation. */
export const CatalogImportReview: FunctionComponent<CatalogImportReviewProps> = ({ summary }) => {
  const { counts, errors, warnings, measures, batteries, source } = summary
  const hasErrors = errors.length > 0

  return (
    <View testID={tID('NpProgramAdmin-review')} style={{ gap: 16 }}>
      {hasErrors ? (
        <InlineErrorBanner
          testId={tID('NpProgramAdmin-status-blocked')}
          text={`${errors.length} ${errors.length === 1 ? 'error' : 'errors'} found in ${counts.rows_with_errors} ${
            counts.rows_with_errors === 1 ? 'row' : 'rows'
          }. Fix the sheet and run the preview again; nothing can be imported until then.`}
        />
      ) : (
        <InlineSuccessBanner
          testId={tID('NpProgramAdmin-status-ready')}
          text='No errors found. Review the changes below, then import the catalog.'
        />
      )}
      {source && <BodyText text={`Sheet tab: ${source.tab}`} size={BodyTextSize.SMALL} />}
      <StatGroups counts={counts} />

      {hasErrors && (
        <Section id='errors' title={`Errors (${errors.length})`} open>
          <Table
            data={errors.map((error) => ({
              row: error.row,
              internal_id: error.internal_id,
              column: error.column,
              message: error.message,
            }))}
            columns={[
              { Header: 'Row', accessor: 'row' },
              { Header: 'TM_ID', accessor: 'internal_id' },
              { Header: 'Column', accessor: 'column' },
              { Header: 'Message', accessor: 'message' },
            ]}
          />
        </Section>
      )}

      {warnings.length > 0 && (
        <Section id='warnings' title={`Warnings (${warnings.length})`}>
          {warnings.map((warning) => (
            <InlineWarningBanner
              key={`${warning.code}-${warning.message}`}
              text={`${warning.message}${warning.rows.length ? ` (rows ${warning.rows.join(', ')})` : ''}`}
            />
          ))}
        </Section>
      )}

      {measures.created.length > 0 && (
        <Section id='measures-created' title={`Measures to create (${measures.created.length})`}>
          <Table
            data={measures.created}
            columns={[
              { Header: 'TM_ID', accessor: 'internal_id' },
              { Header: 'Name', accessor: 'name' },
              { Header: 'Row', accessor: 'row' },
            ]}
          />
        </Section>
      )}

      {measures.updated.length > 0 && (
        <Section id='measures-updated' title={`Measures to update (${measures.updated.length})`}>
          <Table
            data={measures.updated.flatMap(({ internal_id, name, changes }) =>
              Object.entries(changes).map(([field, { from, to }]) => ({
                internal_id,
                name,
                field,
                change: `${formatValue(from)} → ${formatValue(to)}`,
              })),
            )}
            columns={[
              { Header: 'TM_ID', accessor: 'internal_id' },
              { Header: 'Name', accessor: 'name' },
              { Header: 'Field', accessor: 'field' },
              { Header: 'Change', accessor: 'change' },
            ]}
          />
        </Section>
      )}

      {measures.not_in_sheet.length > 0 && (
        <Section
          id='measures-not-in-sheet'
          title={`Active measures not in the sheet (${measures.not_in_sheet.length})`}
        >
          <BodyText text='These measures are only reported. The import does not change them.' />
          <Table
            data={measures.not_in_sheet}
            columns={[
              { Header: 'TM_ID', accessor: 'internal_id' },
              { Header: 'Name', accessor: 'name' },
              { Header: 'Status', accessor: 'status' },
            ]}
          />
        </Section>
      )}

      {batteries.changed.length > 0 && (
        <Section id='batteries' title={`Standard batteries (${batteries.changed.length})`} open>
          {/* The name already carries modality, condition and age band; DataTable columns are a fixed 200px wide,
              so extra columns overflow sideways and detach the header from the body. */}
          <Table
            data={batteries.changed.map((battery) => ({
              action: battery.action,
              name: battery.name,
              measure_count: battery.measure_count,
              added: battery.measures_added.join(', '),
              removed: battery.measures_removed.join(', '),
            }))}
            columns={[
              { Header: 'Action', accessor: 'action' },
              { Header: 'Name', accessor: 'name' },
              { Header: 'Measures', accessor: 'measure_count' },
              { Header: 'Added', accessor: 'added' },
              { Header: 'Removed', accessor: 'removed' },
            ]}
          />
        </Section>
      )}

      {batteries.deprecated.length > 0 && (
        <Section id='batteries-deprecated' title={`Batteries to deprecate (${batteries.deprecated.length})`} open>
          <InlineWarningBanner text='These batteries are no longer produced by the sheet and will be deprecated.' />
          <Table data={batteries.deprecated} columns={[{ Header: 'Name', accessor: 'name' }]} />
        </Section>
      )}

      <RawResponseSection summary={summary} />
    </View>
  )
}
